import { API_BASE_URL } from '../config/api';

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

class ApiService {
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`;
    const config: RequestInit = {
      headers: this.getAuthHeaders(),
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Auth endpoints
  async login(email: string, password: string) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  // User management endpoints
  async getUsers(filters?: { status?: string; role?: string; page?: number; limit?: number; centre_id?: string }) {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.role) params.append('role', filters.role);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.centre_id) params.append('centre_id', filters.centre_id);

    const queryString = params.toString();
    return this.request(`/admin/users${queryString ? `?${queryString}` : ''}`);
  }

  async getDeletedUsers(page = 1, limit = 20) {
    return this.request(`/admin/users/deleted?page=${page}&limit=${limit}`);
  }

  async approveUser(userId: string, notes?: string) {
    return this.request(`/admin/users/${userId}/approve`, {
      method: 'PUT',
      body: JSON.stringify({ notes }),
    });
  }

  async rejectUser(userId: string, reason?: string) {
    return this.request(`/admin/users/${userId}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  }

  async suspendUser(userId: string, reason?: string) {
    return this.request(`/admin/users/${userId}/suspend`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  }

  async activateUser(userId: string, notes?: string) {
    return this.request(`/admin/users/${userId}/activate`, {
      method: 'PUT',
      body: JSON.stringify({ notes }),
    });
  }

  async softDeleteUser(userId: string, reason?: string) {
    return this.request(`/admin/users/${userId}/soft-delete`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    });
  }

  async permanentlyDeleteUser(userId: string) {
    return this.request(`/admin/users/${userId}/permanent-delete`, {
      method: 'DELETE',
    });
  }

  async getUserById(userId: string) {
    return this.request(`/admin/users/${userId}`);
  }

  async createAdmin(adminData: {
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
    password: string;
    role: string;
  }) {
    return this.request('/admin/users/create-admin', {
      method: 'POST',
      body: JSON.stringify(adminData),
    });
  }

  // Bulk Communication methods
  async sendBulkSMS(data: {
    message: string;
    recipient_roles?: string[];
    recipient_ids?: string[];
    schedule_at?: string;
  }) {
    return this.request('/bulk-communication/sms', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async sendBulkEmail(data: {
    subject: string;
    message: string;
    recipient_roles?: string[];
    recipient_ids?: string[];
    schedule_at?: string;
  }) {
    return this.request('/bulk-communication/email', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getCommunicationHistory(type?: string, page = 1, limit = 20) {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    return this.request(`/bulk-communication/history?${params.toString()}`);
  }

  async getCommunicationDetails(id: string) {
    return this.request(`/bulk-communication/${id}`);
  }

  async getRecipientCount(roles?: string[], userIds?: string[]) {
    const params = new URLSearchParams();
    if (roles && roles.length > 0) {
      roles.forEach(role => params.append('recipient_roles', role));
    }
    if (userIds && userIds.length > 0) {
      userIds.forEach(id => params.append('recipient_ids', id));
    }
    return this.request(`/bulk-communication/recipients/count?${params.toString()}`);
  }

  async getUsersByRoles(roles: string[]) {
    const params = new URLSearchParams();
    roles.forEach(role => params.append('roles', role));
    return this.request(`/admin/users/by-roles?${params.toString()}`);
  }

  // Reports API methods
  async getReport(reportType: string, queryParams?: string) {
    const params = queryParams ? `?${queryParams}` : '';
    return this.request(`/reports/${reportType}${params}`);
  }

  async exportReport(reportType: string, dateFrom?: string, dateTo?: string) {
    const params = new URLSearchParams();
    params.append('reportType', reportType);
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    return this.request(`/reports/export?${params.toString()}`);
  }

  // Generic HTTP methods
  async get<T>(endpoint: string): Promise<T> {
    const response = await this.request<T>(endpoint);
    return response.data;
  }

  async post<T>(endpoint: string, data: any): Promise<T> {
    const response = await this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async put<T>(endpoint: string, data: any): Promise<T> {
    const response = await this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async delete<T>(endpoint: string): Promise<T> {
    const response = await this.request<T>(endpoint, {
      method: 'DELETE',
    });
    return response.data;
  }

  // Admin management endpoints
  async getAllAdmins() {
    return this.request('/admin/admins');
  }

  async updateAdminCentre(adminId: string, centreId: string) {
    return this.request(`/admin/admins/${adminId}/centre`, {
      method: 'PUT',
      body: JSON.stringify({ centre_id: centreId }),
    });
  }

  async updateAdminStatus(adminId: string, status: 'active' | 'inactive') {
    return this.request(`/admin/admins/${adminId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  // Centres endpoints
  async getCentres() {
    return this.request('/centres');
  }

  // FormData request (for file uploads — no Content-Type header so browser sets boundary)
  async requestFormData<T>(endpoint: string, method: string, formData: FormData): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = localStorage.getItem('token');
    const response = await fetch(url, {
      method,
      body: formData,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'API request failed');
    return data;
  }

  // Articles API
  async getArticles(params?: { status?: string; tag?: string; search?: string; limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (params?.status && params.status !== 'all') qs.append('status', params.status);
    if (params?.tag) qs.append('tag', params.tag);
    if (params?.search) qs.append('search', params.search);
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.offset) qs.append('offset', String(params.offset));
    const query = qs.toString();
    return this.request(`/articles${query ? `?${query}` : ''}`);
  }

  async getArticle(id: number) {
    return this.request(`/articles/${id}`);
  }

  async createArticle(formData: FormData) {
    return this.requestFormData('/articles', 'POST', formData);
  }

  async updateArticle(id: number, formData: FormData) {
    return this.requestFormData(`/articles/${id}`, 'PUT', formData);
  }

  async deleteArticle(id: number) {
    return this.request(`/articles/${id}`, { method: 'DELETE' });
  }

  // Article Tags API
  async getArticleTags(search?: string) {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request(`/article-tags${qs}`);
  }

  async createArticleTag(name: string) {
    return this.request('/article-tags', { method: 'POST', body: JSON.stringify({ name }) });
  }

  async updateArticleTag(id: number, name: string) {
    return this.request(`/article-tags/${id}`, { method: 'PUT', body: JSON.stringify({ name }) });
  }

  async deleteArticleTag(id: number) {
    return this.request(`/article-tags/${id}`, { method: 'DELETE' });
  }

  // Article Categories API
  async getArticleCategories(search?: string) {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request(`/article-categories${qs}`);
  }

  async createArticleCategory(data: { name: string; color?: string; sort_order?: number }) {
    return this.request('/article-categories', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateArticleCategory(id: number, data: { name: string; color?: string; sort_order?: number }) {
    return this.request(`/article-categories/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteArticleCategory(id: number) {
    return this.request(`/article-categories/${id}`, { method: 'DELETE' });
  }
}

export const apiService = new ApiService();
export default apiService;
